import React, { useEffect, useState } from "react";
import { RotateCcw, Save } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { getEstimator, saveEstimator, resetEstimator } from "../lib/api";
import { estimate, fmtUsd, fmtPct } from "../lib/estimator";

const cell = "w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right outline-none focus:border-[#2f74c9]";

export default function EstimatorAdmin() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [zone, setZone] = useState("pdc_centro");
  useEffect(() => { getEstimator().then(setS).catch(() => setS(null)); }, []);
  if (!s) return <p className="text-slate-400 py-10 text-center">Chargement…</p>;

  const setField = (k, v) => setS({ ...s, [k]: v });
  const setBench = (i, k, v) => {
    const benchmarks = s.benchmarks.map((b, j) => (j === i ? { ...b, [k]: v } : b));
    setS({ ...s, benchmarks });
  };
  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        ...s,
        fee_pct: Number(s.fee_pct), platform_pct: Number(s.platform_pct), avg_stay_nights: Number(s.avg_stay_nights),
        cleaning_per_stay: Object.fromEntries(Object.entries(s.cleaning_per_stay).map(([k, v]) => [k, Number(v) || 0])),
        benchmarks: s.benchmarks.map((b) => ({ ...b, adr: Number(b.adr) || 0, adr_high: Number(b.adr_high) || 0, occ: Math.min(1, Number(b.occ) || 0), occ_high: Math.min(1, Number(b.occ_high) || 0) })),
      };
      setS(await saveEstimator(clean));
      toast({ title: "Simulateur enregistré" });
    } catch { toast({ title: "Erreur", description: "Enregistrement impossible." }); }
    setSaving(false);
  };
  const reset = async () => {
    if (!window.confirm("Rétablir les valeurs de marché par défaut ? Vos modifications seront perdues.")) return;
    setS(await resetEstimator());
    toast({ title: "Valeurs par défaut rétablies" });
  };
  const preview = estimate(s, { zone, bedrooms: "1", hoaMonthly: 0, price: 0 });

  return (
    <div data-testid="estimator-admin">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-serif-display text-2xl text-[#0b1f33]">Simulateur de revenus</h2>
        <div className="flex gap-2">
          <button onClick={reset} data-testid="estimator-reset-btn" className="inline-flex items-center gap-2 text-sm border border-slate-200 hover:border-[#2f74c9] px-4 py-2 rounded-md"><RotateCcw size={14} /> Valeurs par défaut</button>
          <button onClick={save} disabled={saving} data-testid="estimator-save-btn" className="inline-flex items-center gap-2 bg-[#1156A7] hover:bg-[#0e478a] text-white text-sm font-600 px-5 py-2 rounded-md disabled:opacity-60"><Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 p-5 mb-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[["fee_pct", "Frais de gestion (%)"], ["platform_pct", "Frais plateformes (%)"], ["avg_stay_nights", "Séjour moyen (nuits)"]].map(([k, label]) => (
          <label key={k} className="text-xs text-slate-500">{label}
            <input type="number" step="0.5" value={s[k]} onChange={(e) => setField(k, e.target.value)} data-testid={`estimator-${k}`} className={`${cell} mt-1`} />
          </label>
        ))}
        <label className="text-xs text-slate-500 md:col-span-1 col-span-2">Sources affichées
          <input value={s.sources} onChange={(e) => setField("sources", e.target.value)} className={`${cell} mt-1 text-left`} />
        </label>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 p-5 mb-5">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Ménage & linge par séjour (USD)</p>
        <div className="grid grid-cols-5 gap-3">
          {Object.entries(s.bedroom_labels).map(([k, label]) => (
            <label key={k} className="text-xs text-slate-500">{label}
              <input type="number" value={s.cleaning_per_stay[k] ?? ""} onChange={(e) => setS({ ...s, cleaning_per_stay: { ...s.cleaning_per_stay, [k]: e.target.value } })} className={`${cell} mt-1`} />
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <p className="text-xs uppercase tracking-wider text-slate-500">Tarifs & occupation par secteur</p>
          <select value={zone} onChange={(e) => setZone(e.target.value)} data-testid="estimator-admin-zone" className="rounded-md border border-slate-200 px-3 py-2 text-sm bg-white">
            {s.zones.map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
          </select>
        </div>
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-xs"><tr><th className="text-left py-2">Type</th><th className="py-2">Tarif/nuit marché</th><th className="py-2">Tarif/nuit optimisé</th><th className="py-2">Occupation marché</th><th className="py-2">Occupation optimisée</th></tr></thead>
          <tbody>
            {s.benchmarks.map((b, i) => b.zone === zone && (
              <tr key={`${b.zone}-${b.bedrooms}`} className="border-t border-slate-100">
                <td className="py-2 text-[#0b1f33]">{s.bedroom_labels[String(b.bedrooms)]}</td>
                <td className="py-2 px-1"><input type="number" value={b.adr} onChange={(e) => setBench(i, "adr", e.target.value)} data-testid={`bench-adr-${b.zone}-${b.bedrooms}`} className={cell} /></td>
                <td className="py-2 px-1"><input type="number" value={b.adr_high} onChange={(e) => setBench(i, "adr_high", e.target.value)} className={cell} /></td>
                <td className="py-2 px-1"><input type="number" step="0.01" min="0" max="1" value={b.occ} onChange={(e) => setBench(i, "occ", e.target.value)} className={cell} /></td>
                <td className="py-2 px-1"><input type="number" step="0.01" min="0" max="1" value={b.occ_high} onChange={(e) => setBench(i, "occ_high", e.target.value)} className={cell} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {preview && <p className="text-xs text-slate-500 mt-4" data-testid="estimator-admin-preview">Aperçu 1 chambre dans ce secteur : net {fmtUsd(preview.low.netMonthly)} – {fmtUsd(preview.high.netMonthly)} / mois ({fmtPct(preview.low.occ)} – {fmtPct(preview.high.occ)} d'occupation). Occupation saisie en fraction (0,45 = 45 %).</p>}
      </div>
    </div>
  );
}
